<template>
  <!-- Header search expansion. Lives inside the App.vue sticky header and
       takes over the full row when open. Pattern: magnifier icon button
       in the header is the trigger; this component renders the input
       inline, replacing the header content while focused. Esc closes.
       Submit (Enter) routes to /discover?q=<text>.

       Opens: parent flips `modelValue` to true. Auto-focus + caret-at-end on
       open so re-opening from a results page lets the user edit/fix a typo
       in the previous query instead of retyping it (the in-field × clears
       for a fresh search). -->
  <!-- v-show (not v-if) so the <input> stays in the DOM. Mobile browsers
       (especially iOS Safari) refuse to open the on-screen keyboard for a
       focus() call that lands outside a user-gesture stack. With v-if we
       had to await nextTick after mount before we could focus, breaking
       the gesture; with v-show we focus the existing input synchronously
       in the same tick the modelValue flips. -->
  <div
    ref="overlayRef"
    v-show="modelValue"
    class="header-search-overlay"
    :class="{ 'header-search-overlay--tv': isTvMode }"
    role="search"
    data-tv-row="header-search"
    :data-modal-open="remoteNavActive ? '' : null"
  >
    <div class="header-search-overlay__panel">
    <div class="header-search-overlay__inner">
      <!-- Pill-shaped input group: icon + input share one rounded
           container so the search reads as a single targetable element
           instead of three floating siblings. -->
      <div class="header-search-overlay__field">
        <svg
          aria-hidden="true"
          class="header-search-overlay__icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M21 21l-4.35-4.35M16.5 10.5a6 6 0 11-12 0 6 6 0 0112 0z" />
        </svg>

        <input
          ref="inputRef"
          v-model="query"
          type="search"
          :placeholder="placeholder"
          autocomplete="off"
          aria-label="Search movies and TV shows"
          role="combobox"
          aria-autocomplete="list"
          :aria-expanded="showSuggestions"
          :aria-controls="showSuggestions ? 'header-search-suggestions' : undefined"
          :aria-activedescendant="activeIndex >= 0 ? `header-search-opt-${activeIndex}` : undefined"
          :readonly="tvLocked"
          :inputmode="tvLocked ? 'none' : undefined"
          :data-remote-nav-lock="tvLocked ? '' : null"
          data-tv-managed
          class="header-search-overlay__input"
          @keydown.esc.stop.prevent="close"
          @keydown.enter.stop.prevent="onEnter"
          @keydown.down.stop.prevent="moveActive(1)"
          @keydown.up.stop.prevent="moveActive(-1)"
          @click="tvUnlock"
        />

        <button
          v-if="query"
          type="button"
          class="header-search-overlay__clear"
          aria-label="Clear search"
          @click="clearSearch"
        >
          <svg aria-hidden="true" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
              d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Submit / search button. Primary affordance for TV-remote users who
           D-pad over and press OK to fire the search (the input itself isn't
           a great D-pad target since most TV remotes have no Enter-on-keyboard
           equivalent in a text field). Disabled until there's something to
           search for, then it lights up in the accent color so it reads as
           the next step. -->
      <button
        type="button"
        class="header-search-overlay__btn header-search-overlay__btn--submit"
        :disabled="!query.trim()"
        aria-label="Search"
        @click="submit"
      >
        <svg aria-hidden="true" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M21 21l-4.35-4.35M16.5 10.5a6 6 0 11-12 0 6 6 0 0112 0z" />
        </svg>
      </button>

      <!-- Esc-equivalent close button outside the field, signposted as
           the dismiss control rather than an in-field clear. -->
      <button
        type="button"
        class="header-search-overlay__btn header-search-overlay__btn--ghost"
        aria-label="Close search"
        @click="close"
      >
        <svg aria-hidden="true" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

      <!-- Typeahead suggestions. Live TMDB multi-search results render as the
           user types (debounced); click or D-pad+Enter jumps straight to the
           title's detail page. Plain Enter with nothing highlighted still runs
           the full /discover?q= search. Hidden until there are ≥2 chars and at
           least one result (or while a fetch is in flight, to show the
           loading row instead of a layout jump). -->
      <!-- `overflow-y-auto` utility class (not just the scoped overflow-y
           property) so the site-wide scrollbar styling in main.css —
           keyed on `[class*="overflow-y-auto"]` — matches this scroller
           too, instead of falling back to the default browser bar. -->
      <div
        v-if="showSuggestions"
        id="header-search-suggestions"
        class="header-search-overlay__suggestions overflow-y-auto"
        role="listbox"
        aria-label="Search suggestions"
        :aria-busy="suggestLoading"
      >
        <template v-for="(opt, i) in options" :key="opt.key">
          <!-- "Ask AI" row — describe a vibe/plot and get AI recommendations
               grounded in real TMDB titles. Renders first, visually distinct. -->
          <button
            v-if="opt.kind === 'ai'"
            :id="`header-search-opt-${i}`"
            type="button"
            role="option"
            :aria-selected="i === activeIndex"
            class="header-search-overlay__suggestion header-search-overlay__suggestion--ai"
            :class="{ 'is-active': i === activeIndex }"
            @mouseenter="activeIndex = i"
            @click="activateOption(opt)"
          >
            <span class="header-search-overlay__ai-icon" aria-hidden="true">✨</span>
            <span class="header-search-overlay__suggestion-text">
              <span class="header-search-overlay__suggestion-title">Ask AI</span>
              <span class="header-search-overlay__suggestion-meta">
                Find movies &amp; shows matching “{{ query.trim() }}”
              </span>
            </span>
          </button>
          <!-- TMDB title typeahead row. -->
          <button
            v-else
            :id="`header-search-opt-${i}`"
            type="button"
            role="option"
            :aria-selected="i === activeIndex"
            class="header-search-overlay__suggestion"
            :class="{ 'is-active': i === activeIndex }"
            @mouseenter="activeIndex = i"
            @click="activateOption(opt)"
          >
            <img
              :src="opt.item.poster"
              :alt="opt.item.title"
              loading="lazy"
              decoding="async"
              class="header-search-overlay__suggestion-poster"
              @error="onPosterError"
            />
            <span class="header-search-overlay__suggestion-text">
              <span class="header-search-overlay__suggestion-title">{{ opt.item.title }}</span>
              <span class="header-search-overlay__suggestion-meta">
                <span class="header-search-overlay__suggestion-type">{{ opt.item.media_type === 'tv' ? 'TV' : 'Movie' }}</span>
                <span v-if="opt.item.year">· {{ opt.item.year }}</span>
                <span v-if="opt.item.vote_average && opt.item.vote_count >= 50">· ★ {{ opt.item.vote_average.toFixed(1) }}</span>
              </span>
            </span>
          </button>
        </template>
        <div v-if="suggestLoading && suggestions.length === 0" class="header-search-overlay__suggest-status" role="status">
          Searching…
        </div>
        <div v-else-if="suggestError" class="header-search-overlay__suggest-status" role="status">
          Suggestions are temporarily unavailable. You can still run a full search.
          <button type="button" class="header-search-overlay__retry" @click="retrySuggestions">Try again</button>
        </div>
        <div
          v-else-if="!suggestLoading && suggestions.length === 0 && !showAiOption"
          class="header-search-overlay__suggest-status"
          role="status"
        >
          No matches for “{{ query.trim() }}”.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, computed, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { isRemoteNavPreferred } from '@/helpers/remote-nav'
import { useUiStore } from '@/stores/ui'
import { useSettingsStore } from '@/stores/settings'
import { useFocusTrap } from '@/composables/useFocusTrap'
import { useTypeaheadSearch } from '@/composables/useTypeaheadSearch'
import { handoffFromItem, setBrowseHandoff } from '@/helpers/browse-handoff'
// tmdb.js (~52 KB) is heavy and only needed once the user actually types a
// query. Loading it lazily here keeps it (and its transitive deps) out of the
// eager entry chunk — this overlay is mounted app-wide but search is a rare,
// explicit action.

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  placeholder: { type: String, default: 'Search movies and TV shows…' },
})

const emit = defineEmits(['update:modelValue'])

const router = useRouter()
const route = useRoute()
const uiStore = useUiStore()
const settings = useSettingsStore()

const overlayRef = ref(null)
const restoreSearchFocus = ref(true)
const tvLocked = ref(false)
const inputRef = ref(null)
const query = ref('')
const isTvMode = computed(() => uiStore.tvMode)
const remoteNavActive = computed(() => isRemoteNavPreferred(uiStore))

// --- Typeahead suggestions --------------------------------------------------
// Live TMDB multi-search as the user types. Debounced so a fast typist fires
// one request per pause, not one per keystroke; a monotonic request id drops
// stale responses so an earlier slow query can't overwrite a newer one.
const MIN_QUERY_LEN = 2
const activeIndex = ref(-1)
const {
  items: suggestions,
  loading: suggestLoading,
  error: suggestError,
  hasSearched,
  refresh: refreshSuggestions,
  reset: resetSuggestions,
} = useTypeaheadSearch(query, {
  enabled: () => props.modelValue && !tvLocked.value,
  context: () => settings.language,
  search: async (term) => {
    const { searchTitleSuggestions } = await import('@/services/tmdb.js')
    return searchTitleSuggestions(term, { language: settings.language })
  },
})

useFocusTrap(overlayRef, {
  active: () => props.modelValue,
  autoFocus: false,
  returnFocus: () => restoreSearchFocus.value,
  onClose: close,
})

// Show the dropdown only once the user has typed something searchable, and
// only while the overlay is open. Stays visible during loading so the panel
// doesn't flicker, and to host the "No matches" / "Searching…" status rows.
const showSuggestions = computed(
  () =>
    props.modelValue &&
    !tvLocked.value &&
    query.value.trim().length >= MIN_QUERY_LEN &&
    (showAiOption.value ||
      suggestLoading.value ||
      suggestions.value.length > 0 ||
      hasSearched.value),
)

// AI "Ask AI" affordance. Availability is probed once per open via the
// session-cached status endpoint; the row only appears when the user has the
// suggest-movies permission AND the server has a resolvable Gemini key.
const aiConfigured = ref(false)
const showAiOption = computed(
  () =>
    props.modelValue &&
    !tvLocked.value &&
    aiConfigured.value &&
    query.value.trim().length >= MIN_QUERY_LEN,
)
// Combined list the dropdown + keyboard nav iterate over: the AI row (when
// available) first, then the TMDB title typeahead. Keeps one activeIndex across
// both so D-pad / arrow nav flows through the sparkle row into the titles.
const options = computed(() => {
  const opts = []
  if (showAiOption.value) opts.push({ kind: 'ai', key: 'ai' })
  for (const item of suggestions.value) {
    opts.push({ kind: 'title', item, key: `${item.media_type}-${item.id}` })
  }
  return opts
})

watch(query, () => { activeIndex.value = -1 }, { flush: 'sync' })
watch(options, () => { activeIndex.value = -1 })

function moveActive(delta) {
  const n = options.value.length
  if (n === 0) return
  // -1 (input) → wrap through the list. From the input, Down goes to first,
  // Up goes to last; movement past either end returns focus to the input.
  let next = activeIndex.value + delta
  if (next < -1) next = n - 1
  if (next >= n) next = -1
  activeIndex.value = next
  nextTick(() => {
    overlayRef.value?.querySelector(`#header-search-opt-${next}`)?.scrollIntoView({ block: 'nearest', behavior: 'instant' })
  })
}

function goToSuggestion(item) {
  if (!item || !item.media_type || !item.id) return
  setBrowseHandoff(item.media_type, item.id, handoffFromItem(item))
  resetSuggestions()
  router.push(`/browse/${item.media_type}/${item.id}`)
  close({ restoreFocus: false })
}

// Route to the Discover AI results grid (?ai=<prose>). DiscoverView's
// filterLoader posts the prose to /api/suggest and renders grounded TMDB rows.
function submitAi() {
  const trimmed = (query.value || '').trim()
  if (!trimmed) return
  resetSuggestions()
  router.push({ name: 'discover', query: { ai: trimmed } })
  close({ restoreFocus: false })
}

// Activate whichever combined-list option is chosen (click or Enter).
function activateOption(opt) {
  if (!opt) return
  if (opt.kind === 'ai') submitAi()
  else if (opt.kind === 'title') goToSuggestion(opt.item)
}

function onPosterError(e) {
  if (e?.target) e.target.src = '/placeholder.svg'
}

function onOutsidePointer(event) {
  if (props.modelValue && !overlayRef.value?.contains(event.target)) close({ restoreFocus: false })
}

onUnmounted(() => {
  document.removeEventListener('pointerdown', onOutsidePointer)
})

// On open: pre-fill from current URL (so users see "what they searched
// for" if they re-open from /discover?q=foo) and auto-focus with the caret
// at the end so they can edit/fix a typo instead of retyping.
//
// `nextTick` is the load-bearing piece here. The component uses
// `v-show` (not v-if) on the outer wrapper, so the input is always in
// the DOM -- but while modelValue is false, v-show paints
// `display: none` on the wrapper. The watch callback fires when the
// prop changes (modelValue: false -> true), but `display: none` is
// still in place at that moment because the v-show binding is applied
// during Vue's next render pass, not synchronously. A direct
// `el.focus()` call silently fails because browsers refuse to focus
// elements inside a display:none ancestor.
//
// `nextTick` pushes the focus call past Vue's render pass, so by the
// time we ask for focus the overlay is actually visible. nextTick
// runs as a microtask inside the same task as the click handler that
// flipped `showHeaderSearch`, so mobile browsers still treat it as
// part of the original user gesture and auto-open the on-screen
// keyboard.
watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      restoreSearchFocus.value = true
      activeIndex.value = -1
      // Prefill from the active AI prose (?ai=) or text query (?q=) so
      // re-opening from a results page lets the user refine what they asked.
      query.value = String(route.query?.ai || route.query?.q || '')
      tvLocked.value = remoteNavActive.value
      // Probe AI-suggest availability (permission + server key). Cached per
      // session; a 403/miss just leaves the "Ask AI" row hidden.
      import('@/services/movieSuggest.js')
        .then(({ getSuggestStatus }) => getSuggestStatus())
        .then((available) => { aiConfigured.value = available })
        .catch(() => {})
      document.addEventListener('pointerdown', onOutsidePointer)
      nextTick(() => {
        const el = inputRef.value
        if (!el || !props.modelValue) return
        el.focus()
        // Place the caret at the END of any prefilled text (don't select-all)
        // so re-opening from a results page lets the user EDIT/fix a typo
        // instead of having the first keystroke wipe the whole query
        // (CLAUDE-TODO: "make it easier to edit the search input from the
        // results page"). The in-field clear (×) button handles "start fresh".
        // Skipped while tvLocked so the IME isn't tempted to open on some TV
        // browsers that ignore readonly.
        if (!tvLocked.value) {
          try {
            const end = el.value.length
            el.setSelectionRange(end, end)
          } catch { /* not all browsers expose setSelectionRange on type=search */ }
        }
      })
    } else {
      // Clear the local input on close so the next open starts fresh
      // unless route.query.q has a value.
      query.value = ''
      tvLocked.value = false
      resetSuggestions()
      document.removeEventListener('pointerdown', onOutsidePointer)
    }
  },
)

function tvUnlock() {
  if (!tvLocked.value) return
  tvLocked.value = false
  // Re-focus on the next tick so the IME picks up the now-editable state.
  nextTick(() => {
    const el = inputRef.value
    if (!el) return
    el.focus()
    try { el.select() } catch { /* not all browsers expose .select() on type=search */ }
  })
}

function onEnter() {
  if (tvLocked.value) {
    tvUnlock()
    return
  }
  // If the user arrow-keyed onto an option (the AI row or a title), Enter
  // activates it. Otherwise Enter runs the full /discover?q= search (unchanged).
  if (activeIndex.value >= 0 && options.value[activeIndex.value]) {
    activateOption(options.value[activeIndex.value])
    return
  }
  submit()
}

function retrySuggestions() {
  refreshSuggestions()
  inputRef.value?.focus({ preventScroll: true })
}

function clearSearch() {
  query.value = ''
  nextTick(() => inputRef.value?.focus({ preventScroll: true }))
}

function close({ restoreFocus = true } = {}) {
  restoreSearchFocus.value = restoreFocus
  emit('update:modelValue', false)
}

function submit() {
  const trimmed = (query.value || '').trim()
  if (!trimmed) {
    close()
    return
  }
  // Route to the Home tab on Discover so search is consistent regardless
  // of which tab the user was on. The Home tab's filterLoader handles the
  // text query against the multi-search endpoint.
  router.push({ name: 'discover', query: { q: trimmed } })
  close({ restoreFocus: false })
}
</script>

<style scoped>
.header-search-overlay { position: fixed; inset: 0 0 auto; left: var(--collection-inset, 0px); display: flex; align-items: flex-start; justify-content: center; z-index: 60; padding: 14px 30px; min-height: 76px; background: var(--c-canvas); border-bottom: 1px solid var(--c-border); box-shadow: var(--shadow-pop); }
.header-search-overlay__panel { display: flex; flex-direction: column; width: 100%; max-width: none; min-width: 0; }
.header-search-overlay__inner { display: flex; align-items: center; gap: 10px; width: 100%; }
.header-search-overlay__field { display: flex; flex: 1; align-items: center; gap: 10px; min-width: 0; min-height: 48px; padding-left: 14px; border: 1px solid var(--c-border-strong); border-radius: 8px; background: var(--c-surface); }
.header-search-overlay__field:focus-within { border-color: var(--c-accent); box-shadow: var(--shadow-glow-sm); }
.header-search-overlay__icon { width: 20px; height: 20px; flex-shrink: 0; color: var(--c-text-2); stroke-width: 2; }
.header-search-overlay__input { flex: 1; min-width: 0; width: 100%; height: 46px; border: 0; padding: 0; outline: 0; background: transparent; color: var(--c-text); font-size: 16px; }
.header-search-overlay .header-search-overlay__input:focus-visible,
.header-search-overlay .header-search-overlay__input[data-spatial-focus] { outline: none !important; box-shadow: none !important; }
.header-search-overlay__input::placeholder { color: var(--c-text-3); }
.header-search-overlay__input::-webkit-search-decoration,
.header-search-overlay__input::-webkit-search-cancel-button { appearance: none; -webkit-appearance: none; }
.header-search-overlay__clear,
.header-search-overlay__btn { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; min-width: 44px !important; min-height: 44px !important; padding: 0 !important; flex-shrink: 0; border: 0; border-radius: 7px; cursor: pointer; }
.header-search-overlay__clear { margin-right: 2px; background: transparent; color: var(--c-text-2); }
.header-search-overlay__clear:hover,
.header-search-overlay__btn--ghost:hover { background: var(--c-accent-soft); color: var(--c-accent); }
.header-search-overlay__btn--ghost { color: var(--c-text-2); background: transparent; }
.header-search-overlay__btn--submit { background: var(--c-accent); color: var(--c-on-accent); }
.header-search-overlay__btn--submit:hover:not(:disabled) { background: var(--c-accent-hover); }
.header-search-overlay__btn--submit:disabled { color: var(--c-text-disabled); background: var(--c-surface); cursor: not-allowed; }
.header-search-overlay__suggestions { margin-top: 12px; padding: 6px; border: 1px solid var(--c-border); border-radius: 10px; background: var(--c-surface); max-height: min(62dvh, 440px); overscroll-behavior: contain; }
.header-search-overlay__suggestion { display: flex; align-items: center; gap: 12px; width: 100%; min-height: 68px; padding: 8px; border: 0; border-radius: 6px; background: transparent; text-align: left; cursor: pointer; transition: background-color 120ms; }
.header-search-overlay__suggestion:hover,
.header-search-overlay__suggestion.is-active { background: var(--c-accent-soft); }
.header-search-overlay__suggestion-poster { flex-shrink: 0; width: 36px; height: 54px; object-fit: cover; border-radius: 4px; background: var(--c-canvas); }
.header-search-overlay__suggestion-text { display: flex; flex: 1; flex-direction: column; min-width: 0; gap: 4px; }
.header-search-overlay__suggestion-title { overflow: hidden; white-space: nowrap; text-overflow: ellipsis; font-size: .875rem; font-weight: 550; color: var(--c-text); }
.header-search-overlay__suggestion-meta { display: flex; gap: 5px; font-size: .75rem; color: var(--c-text-2); }
.header-search-overlay__suggestion-type { color: var(--c-accent); }
.header-search-overlay__ai-icon { display: grid; place-items: center; flex-shrink: 0; width: 36px; height: 54px; border-radius: 4px; background: var(--c-accent-soft); font-size: 1.25rem; }
.header-search-overlay__suggestion--ai .header-search-overlay__suggestion-title { color: var(--c-accent); }
.header-search-overlay__suggest-status { padding: 14px 12px; color: var(--c-text-2); font-size: .8125rem; line-height: 1.6; }
.header-search-overlay__retry { display: block; min-height: 44px; margin-top: 6px; color: var(--c-accent); font-weight: 550; }
.header-search-overlay--tv { left: 0; padding: 18px 28px; }
.header-search-overlay--tv .header-search-overlay__panel { max-width: 1400px; }
.header-search-overlay--tv .header-search-overlay__inner { gap: 14px; }
.header-search-overlay--tv .header-search-overlay__field { min-height: 64px; }
.header-search-overlay--tv .header-search-overlay__input { height: 62px; font-size: 1.2rem; }
.header-search-overlay--tv .header-search-overlay__btn,
.header-search-overlay--tv .header-search-overlay__clear { width: 56px; height: 56px; min-width: 56px !important; min-height: 56px !important; }
.header-search-overlay--tv .header-search-overlay__suggestion { padding: 12px !important; gap: 16px; }
.header-search-overlay--tv .header-search-overlay__suggestion-poster,
.header-search-overlay--tv .header-search-overlay__ai-icon { width: 48px; height: 72px; }
.header-search-overlay--tv .header-search-overlay__suggestion-title { font-size: 1.1rem; }
.header-search-overlay--tv .header-search-overlay__suggestion-meta { font-size: .9rem; }
.header-search-overlay--tv .header-search-overlay__suggestions { max-height: min(64dvh, 560px); }
@media (max-width: 480px) {
  .header-search-overlay { padding-inline: 12px; }
  .header-search-overlay__inner { gap: 6px; }
  .header-search-overlay__field { padding-left: 10px; gap: 7px; }
  .header-search-overlay__icon { display: none; }
}
</style>
